import type { RoutePathOption, RoutePlan } from '@gpx-spa/geo-domain';
import { pathIdForSegment } from '@/utils/device-api-shipment-payload';

/**
 * Driving time is accumulated in `bucketMs`; when it would exceed `maxDriveMs`, a `restMs` break
 * is inserted and the bucket resets. Dwell at middle stops does not touch this bucket (only leg
 * driving from Google `duration_s` does).
 */
export function advanceCalendarMsForDrivingSegment(
  driveMs: number,
  state: { bucketMs: number },
  maxDriveMs: number,
  restMs: number,
): number {
  let calendarMs = 0;
  let remaining = driveMs;
  while (remaining > 0) {
    const room = maxDriveMs - state.bucketMs;
    if (room <= 0) {
      calendarMs += restMs;
      state.bucketMs = 0;
      continue;
    }
    const chunk = Math.min(remaining, room);
    calendarMs += chunk;
    state.bucketMs += chunk;
    remaining -= chunk;
    if (remaining > 0 && state.bucketMs >= maxDriveMs) {
      calendarMs += restMs;
      state.bucketMs = 0;
    }
  }
  return calendarMs;
}

export function calendarMsForPureDrivingMs(
  driveMs: number,
  maxDriveMs: number,
  restMs: number,
): number {
  return advanceCalendarMsForDrivingSegment(
    driveMs,
    { bucketMs: 0 },
    maxDriveMs,
    restMs,
  );
}

/**
 * Finds departure time (ms) such that departure + calendar(driveMs) ≈ arrivalMs.
 * Uses a fresh driving bucket at departure (per-leg backward step in partial graphs).
 */
export function departureMsBeforeArrivalForDrivingSegment(
  arrivalMs: number,
  driveMs: number,
  maxDriveMs: number,
  restMs: number,
): number {
  let lo = arrivalMs - 21 * 24 * 60 * 60 * 1000;
  let hi = arrivalMs;
  for (let i = 0; i < 64; i++) {
    const mid = (lo + hi) / 2;
    const end = mid + calendarMsForPureDrivingMs(driveMs, maxDriveMs, restMs);
    if (end < arrivalMs) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return hi;
}

export type FormPlannedSlot = 'plannedDeparture' | 'plannedArrival';

export function manualSlotKey(
  stopKey: string,
  slot: FormPlannedSlot,
): string {
  return `${stopKey}:${slot}`;
}

export type AnchorStopRowForManualSlots = {
  key: string;
  plannedDeparture: Date | null;
  plannedArrival: Date | null;
};

/** Mark or unmark origin departure + destination arrival in a manual-slots set. */
export function applyAnchorManualSlotKeys(
  manual: ReadonlySet<string>,
  stops: ReadonlyArray<AnchorStopRowForManualSlots>,
  mode: 'mark' | 'unmark',
): Set<string> {
  const next = new Set(manual);
  if (stops.length < 2) {
    return next;
  }
  const first = stops[0]!;
  const last = stops[stops.length - 1]!;
  const depKey = manualSlotKey(first.key, 'plannedDeparture');
  const arrKey = manualSlotKey(last.key, 'plannedArrival');
  if (mode === 'unmark') {
    next.delete(depKey);
    next.delete(arrKey);
    return next;
  }
  if (first.plannedDeparture != null) {
    next.add(depKey);
  }
  if (last.plannedArrival != null) {
    next.add(arrKey);
  }
  return next;
}

function durationSecondsFromPath(p: RoutePathOption | null): number | null {
  if (!p?.meta) return null;
  const s = p.meta.duration_s;
  if (typeof s !== 'number' || !Number.isFinite(s) || s < 0) {
    return null;
  }
  return s;
}

/**
 * Shortest driving time among path options for the leg (among toggled-on paths, or all paths if none toggled).
 */
export function minDrivingSecondsForSegment(
  paths: RoutePathOption[],
  segmentIndex: number,
  selectedByPathId: Record<string, boolean>,
): number | null {
  const withIds = paths.map((p, i) => ({
    p,
    id: pathIdForSegment(segmentIndex, p, i),
  }));
  const selectedOn = withIds.filter((x) => selectedByPathId[x.id] !== false);
  const candidates = selectedOn.length > 0 ? selectedOn : withIds;

  let best: number | null = null;
  for (const { p } of candidates) {
    const d = durationSecondsFromPath(p);
    if (d == null) continue;
    if (best == null || d < best) {
      best = d;
    }
  }
  return best;
}

/**
 * Ceil route driving duration to whole hours (e.g. 9h 23m 22s → 10h) before building the stop timeline.
 */
export function ceilDrivingDurationSecondsToWholeHours(
  durationS: number | null,
): number | null {
  if (durationS == null || !Number.isFinite(durationS)) {
    return durationS;
  }
  if (durationS <= 0) {
    return 0;
  }
  return Math.ceil(durationS / 3600) * 3600;
}

/**
 * Per consecutive stop pair `i → i+1`, driving seconds = minimum `duration_s` among route alternatives
 * (respects path toggles the same way as the composer map), then **ceiled up to whole hours** for autofill.
 */
export function legDrivingSecondsFromRoutePlan(
  plan: RoutePlan | null,
  stopCount: number,
  selectedByPathId: Record<string, boolean>,
): (number | null)[] {
  const n = stopCount;
  const legs = n >= 2 ? n - 1 : 0;
  const out: (number | null)[] = Array.from({ length: legs }, () => null);
  if (!plan || legs === 0) {
    return out;
  }

  for (let leg = 0; leg < legs; leg++) {
    for (let sIdx = 0; sIdx < plan.segments.length; sIdx++) {
      const seg = plan.segments[sIdx];
      if (!seg || seg.fromStopIndex !== leg || seg.toStopIndex !== leg + 1) {
        continue;
      }
      const raw = minDrivingSecondsForSegment(
        seg.paths ?? [],
        sIdx,
        selectedByPathId,
      );
      out[leg] = ceilDrivingDurationSecondsToWholeHours(raw);
      break;
    }
  }
  return out;
}

export type StopTimesSnapshot = {
  key: string;
  plannedDeparture: Date | null;
  plannedArrival: Date | null;
}[];

function hasDepSlot(index: number, n: number): boolean {
  return index < n - 1;
}

function hasArrSlot(index: number): boolean {
  return index > 0;
}

export function computeForwardLastArrivalMs(
  originDepartureMs: number,
  stops: StopTimesSnapshot,
  legDriveS: (number | null)[],
  dwellMs: number,
  maxDriveMs: number,
  restMs: number,
): number | null {
  const n = stops.length;
  if (n < 2) return null;
  for (let i = 0; i < n - 1; i++) {
    if (legDriveS[i] == null) return null;
  }

  const hos = { bucketMs: 0 };
  let t = originDepartureMs;
  for (let i = 0; i < n - 1; i++) {
    const driveMs = (legDriveS[i] as number) * 1000;
    t += advanceCalendarMsForDrivingSegment(driveMs, hos, maxDriveMs, restMs);
    if (i + 1 < n - 1) {
      t += dwellMs;
    }
  }
  return t;
}

export type AutofillPatch = {
  key: string;
  plannedDeparture?: Date | null;
  plannedArrival?: Date | null;
};

function shouldWriteSlot(
  current: Date | null,
  slotLocked: boolean,
  allowOverwriteExistingNonManual: boolean,
): boolean {
  if (slotLocked) return false;
  if (current == null) return true;
  return allowOverwriteExistingNonManual;
}

/**
 * Merges forward (HOS-continuous from origin departure) and backward (per-leg inverse, fresh bucket)
 * passes until stable. Only writes slots allowed by `shouldWriteSlot` and `manualSlots`.
 */
export function computeAutofillPatches(params: {
  stops: StopTimesSnapshot;
  legDriveS: (number | null)[];
  dwellMs: number;
  maxDriveMs: number;
  restMs: number;
  manualSlots: ReadonlySet<string>;
  /** Stop keys already persisted on device-api — do not overwrite planned times from autofill. */
  persistedStopKeys: ReadonlySet<string>;
  /** Route or HOS/dwell settings changed — may refresh prior autofill values. */
  routeRefresh: boolean;
  /** User (or program) changed at least one planned time since last autofill — re-derive non-manual filled slots. */
  plannedTimesChanged: boolean;
}): AutofillPatch[] {
  const {
    stops,
    legDriveS,
    dwellMs,
    maxDriveMs,
    restMs,
    manualSlots,
    persistedStopKeys,
    routeRefresh,
    plannedTimesChanged,
  } = params;
  const n = stops.length;
  if (n < 2) return [];

  const allowOverwriteExistingNonManual = routeRefresh || plannedTimesChanged;

  const dep: (number | null)[] = stops.map((s) =>
    s.plannedDeparture ? s.plannedDeparture.getTime() : null,
  );
  const arr: (number | null)[] = stops.map((s) =>
    s.plannedArrival ? s.plannedArrival.getTime() : null,
  );

  const depLocked = (i: number) =>
    manualSlots.has(manualSlotKey(stops[i]!.key, 'plannedDeparture')) ||
    persistedStopKeys.has(stops[i]!.key);
  const arrLocked = (i: number) =>
    manualSlots.has(manualSlotKey(stops[i]!.key, 'plannedArrival')) ||
    persistedStopKeys.has(stops[i]!.key);

  const canWriteDep = (i: number) =>
    hasDepSlot(i, n) &&
    shouldWriteSlot(
      stops[i]!.plannedDeparture,
      depLocked(i),
      allowOverwriteExistingNonManual,
    );
  const canWriteArr = (i: number) =>
    hasArrSlot(i) &&
    shouldWriteSlot(
      stops[i]!.plannedArrival,
      arrLocked(i),
      allowOverwriteExistingNonManual,
    );

  for (let round = 0; round < 24; round++) {
    let changed = false;

    const dep0 = dep[0];
    if (dep0 != null) {
      const hos = { bucketMs: 0 };
      let t = dep0;
      for (let i = 0; i < n - 1; i++) {
        const dS = legDriveS[i];
        if (dS == null) {
          break;
        }
        const driveMs = dS * 1000;
        t += advanceCalendarMsForDrivingSegment(
          driveMs,
          hos,
          maxDriveMs,
          restMs,
        );
        const ai = i + 1;
        if (canWriteArr(ai)) {
          if (arr[ai] !== t) {
            arr[ai] = t;
            changed = true;
          }
        } else if (arr[ai] == null) {
          break;
        } else {
          t = arr[ai]!;
        }
        if (ai >= n - 1) {
          break;
        }
        const nextDep = t + dwellMs;
        if (canWriteDep(ai)) {
          if (dep[ai] !== nextDep) {
            dep[ai] = nextDep;
            changed = true;
          }
          t = dep[ai]!;
        } else if (dep[ai] == null) {
          break;
        } else {
          t = dep[ai]!;
        }
      }
    }

    const lastArr = arr[n - 1];
    if (lastArr != null) {
      let t = lastArr;
      for (let leg = n - 2; leg >= 0; leg--) {
        const dS = legDriveS[leg];
        if (dS == null) {
          break;
        }
        const driveMs = dS * 1000;
        const depMs = departureMsBeforeArrivalForDrivingSegment(
          t,
          driveMs,
          maxDriveMs,
          restMs,
        );
        const i = leg;
        let effectiveDep = dep[i];
        if (canWriteDep(i)) {
          if (dep[i] !== depMs) {
            dep[i] = depMs;
            changed = true;
          }
          effectiveDep = dep[i];
        } else if (dep[i] == null) {
          break;
        } else {
          effectiveDep = dep[i];
        }
        if (effectiveDep == null) {
          break;
        }
        if (i === 0) {
          break;
        }
        const arrMs = effectiveDep - dwellMs;
        if (canWriteArr(i)) {
          if (arr[i] !== arrMs) {
            arr[i] = arrMs;
            changed = true;
          }
        } else if (arr[i] == null) {
          break;
        }
        t = arr[i]!;
      }
    }

    if (!changed) {
      break;
    }
  }

  const patches: AutofillPatch[] = [];
  for (let i = 0; i < n; i++) {
    const s = stops[i]!;
    const origD = s.plannedDeparture?.getTime() ?? null;
    const origA = s.plannedArrival?.getTime() ?? null;
    const nextD = hasDepSlot(i, n) ? dep[i] : null;
    const nextA = hasArrSlot(i) ? arr[i] : null;
    const patch: AutofillPatch = { key: s.key };
    if (hasDepSlot(i, n) && nextD !== origD) {
      patch.plannedDeparture = nextD != null ? new Date(nextD) : null;
    }
    if (hasArrSlot(i) && nextA !== origA) {
      patch.plannedArrival = nextA != null ? new Date(nextA) : null;
    }
    if (
      patch.plannedDeparture !== undefined ||
      patch.plannedArrival !== undefined
    ) {
      patches.push(patch);
    }
  }
  return patches;
}

export function allLegDurationsKnown(legDriveS: (number | null)[]): boolean {
  return legDriveS.length > 0 && legDriveS.every((x) => x != null);
}

export function detectAnchorConflict(params: {
  originDepartureMs: number;
  destinationArrivalMs: number;
  stops: StopTimesSnapshot;
  legDriveS: (number | null)[];
  dwellMs: number;
  maxDriveMs: number;
  restMs: number;
  thresholdMs: number;
}): boolean {
  if (!allLegDurationsKnown(params.legDriveS)) {
    return false;
  }
  const computedLast = computeForwardLastArrivalMs(
    params.originDepartureMs,
    params.stops,
    params.legDriveS as number[],
    params.dwellMs,
    params.maxDriveMs,
    params.restMs,
  );
  if (computedLast == null) {
    return false;
  }
  return (
    Math.abs(computedLast - params.destinationArrivalMs) >= params.thresholdMs
  );
}

/**
 * When all leg durations are known, finds origin departure such that the forward chain matches
 * `destinationArrivalMs` (±1s tolerance).
 */
export function solveOriginDepartureMsForDestinationArrival(
  destinationArrivalMs: number,
  stops: StopTimesSnapshot,
  legDriveS: number[],
  dwellMs: number,
  maxDriveMs: number,
  restMs: number,
): number | null {
  if (!allLegDurationsKnown(legDriveS)) {
    return null;
  }
  let lo = destinationArrivalMs - 30 * 24 * 60 * 60 * 1000;
  let hi = destinationArrivalMs + 2 * 24 * 60 * 60 * 1000;

  const f = (dep0: number) =>
    computeForwardLastArrivalMs(
      dep0,
      stops,
      legDriveS,
      dwellMs,
      maxDriveMs,
      restMs,
    ) ?? dep0;

  while (f(lo) > destinationArrivalMs) {
    lo -= 7 * 24 * 60 * 60 * 1000;
  }
  let guard = 0;
  while (f(hi) < destinationArrivalMs && guard++ < 64) {
    hi += 7 * 24 * 60 * 60 * 1000;
  }

  for (let i = 0; i < 80; i++) {
    const mid = (lo + hi) / 2;
    if (f(mid) < destinationArrivalMs) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  const cand = hi;
  const last = f(cand);
  if (last == null || Math.abs(last - destinationArrivalMs) > 2000) {
    return null;
  }
  return cand;
}
